<template>
  <div class="ui-content dashboard timetable-list-page">
    <div class="timetable-top-bar">
      <div class="timetable-top-bar-inner">
        <div class="filter-section">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            @change="onStatusFilterChange"
          ></v-select>
          <v-select
            v-model="selectedCenterFilterIds"
            :items="centers"
            item-text="text"
            item-value="value"
            placeholder="All Centers"
            outlined
            dense
            hide-details
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            @change="onCenterFilterChange"
          ></v-select>
          <v-select
            v-if="selectedCenterFilterIds.length"
            v-model="selectedBatchFilterIds"
            :items="filterBatchOptions"
            item-text="text"
            item-value="value"
            placeholder="All Batches"
            outlined
            dense
            hide-details
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            @change="onBatchFilterChange"
          ></v-select>
        </div>
        <v-btn class="cust-btn" color="primary" @click="openAddDialog">
          Add Timetable
        </v-btn>
      </div>
    </div>
    <v-card class="mx-auto normal-card autohg">
      <div class="data-table-section">
        <v-data-table
          :headers="headers"
          :items="timetableList"
          fixed-header
          hide-default-header
          hide-default-footer
          class="elevation-2 cust-table"
          :items-per-page="-1"
        >
          <template v-slot:header="{ props: { headers } }">
            <TableHeader
              :headers="headers"
              :onHeaderAction="onHeaderAction"
              :orderBy="orderBy"
              :onSortChange="onSortChange"
            />
          </template>

          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.id" :class="{ 'deleted-row': item.is_delete === 1 }">
                <td>
                  <div>{{ item.original_name }}</div>
                  <div v-if="item.is_delete === 1" class="deletion-date">
                    Will be deleted on {{ purgeDate(item.updated_at) }}
                  </div>
                </td>
                <td>
                  <div
                    v-for="(cb, ci) in item.centers_batches"
                    :key="ci"
                    class="center-batch-group"
                  >
                    <div class="center-name">{{ cb.center_name }}</div>
                    <div class="batch-list">{{ cb.batches.map(function(b) { return b.name; }).join(', ') }}</div>
                  </div>
                </td>
                <td>{{ formatDate(item.created_at) }}</td>
                <td>{{ item.uploaded_by }}</td>
                <td class="actions-col cp3-edit-delete-icon">
                  <v-tooltip left content-class="cust-tooltip">
                    <template v-slot:activator="{ on }">
                      <v-icon
                        class="view-icon"
                        v-on="on"
                        @click="onViewClick(item)"
                      >mdi-eye</v-icon>
                    </template>
                    <span>View Timetable</span>
                  </v-tooltip>
                  <v-tooltip v-if="!item.is_delete" left content-class="cust-tooltip">
                    <template v-slot:activator="{ on }">
                      <v-icon
                        class="pencil-icon"
                        v-on="on"
                        @click="onEditClick(item)"
                      >mdi-pencil</v-icon>
                    </template>
                    <span>Edit Timetable</span>
                  </v-tooltip>
                  <v-tooltip v-if="!item.is_delete && canDeleteTimetable(item)" left content-class="cust-tooltip">
                    <template v-slot:activator="{ on }">
                      <v-icon
                        class="delete-icon"
                        v-on="on"
                        @click="onDeleteClick(item)"
                      >mdi-delete</v-icon>
                    </template>
                    <span>Delete Timetable</span>
                  </v-tooltip>
                </td>
              </tr>
              <tr v-if="!items.length && hasLoaded">
                <td :colspan="headers.length" class="text-center pa-6 grey--text">
                  No timetables found.
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
      <div class="pagination-section" v-if="totalPages > 1">
        <Pagination
          :totalPage="totalPages"
          :currentPage="currentPage"
          :onPageChange="onPageChange"
        />
      </div>
    </v-card>

    <!-- Add Timetable Dialog -->
    <v-dialog v-model="addDialog" max-width="700px" @click:outside="closeAddDialog">
      <v-card>
        <v-card-title class="headline">
          Add Timetable
          <v-spacer></v-spacer>
          <v-icon @click="closeAddDialog">mdi-close</v-icon>
        </v-card-title>
        <v-card-text>
          <div class="add-timetable-form">
            <div class="form-row">
              <label>Upload PDF *</label>
              <div class="file-upload-area" @click="$refs.fileInput.click()">
                <input
                  type="file"
                  ref="fileInput"
                  accept="application/pdf"
                  @change="onFileChange"
                  style="display: none"
                />
                <v-icon v-if="!selectedFile" color="grey" class="mr-2">mdi-file-pdf-box</v-icon>
                <span v-if="!selectedFile" class="grey--text">Click to select a PDF file</span>
                <v-icon v-else color="red" class="mr-2">mdi-file-pdf-box</v-icon>
                <span v-if="selectedFile">{{ selectedFile.name }}</span>
                <v-spacer></v-spacer>
                <v-icon v-if="selectedFile" small @click.stop="clearFile">mdi-close</v-icon>
              </div>
              <InputError :data="formErrors.file" />
            </div>
            <div class="form-row">
              <label>Assign to Centers & Batches *</label>
              <div
                v-for="(group, gi) in centerBatchGroups"
                :key="gi"
                class="center-batch-row"
              >
                <v-select
                  v-model="group.centerId"
                  :items="availableCenters(gi)"
                  item-text="text"
                  item-value="value"
                  placeholder="Select Center"
                  outlined
                  dense
                  hide-details
                  class="center-select"
                  @change="onGroupCenterChange(gi)"
                ></v-select>
                <v-select
                  v-if="group.centerId"
                  v-model="group.batchIds"
                  :items="group.batchOptions"
                  item-text="text"
                  item-value="value"
                  placeholder="Select Batches"
                  outlined
                  dense
                  hide-details
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  class="batch-select"
                ></v-select>
                <v-btn
                  v-if="centerBatchGroups.length > 1"
                  icon
                  small
                  color="red"
                  @click="removeGroup(gi)"
                >
                  <v-icon small>mdi-minus-circle</v-icon>
                </v-btn>
              </div>
              <InputError :data="formErrors.batches" />
              <v-btn
                text
                small
                color="primary"
                class="mt-2"
                @click="addGroup"
                :disabled="centerBatchGroups.length >= centers.length"
              >
                <v-icon small left>mdi-plus-circle</v-icon>
                Add Center
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAddDialog">Cancel</v-btn>
          <v-btn color="primary" class="ma-2 cust-btn" @click="submitTimetable" :loading="uploading">Upload</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="700px" @click:outside="closeEditDialog">
      <v-card>
        <v-card-title class="headline">
          Edit Timetable
          <v-spacer></v-spacer>
          <v-icon @click="closeEditDialog">mdi-close</v-icon>
        </v-card-title>
        <v-card-text>
          <div class="add-timetable-form">
            <div class="form-row">
              <label>File Name *</label>
              <v-text-field
                v-model="editName"
                placeholder="Enter file name"
                outlined
                dense
                hide-details
              ></v-text-field>
            </div>
            <div class="form-row">
              <label>Assign to Centers & Batches *</label>
              <div
                v-for="(group, gi) in editCenterBatchGroups"
                :key="gi"
                class="center-batch-row"
              >
                <v-text-field
                  v-if="group.readonly"
                  :value="group.centerName"
                  outlined
                  dense
                  hide-details
                  disabled
                  class="center-select"
                ></v-text-field>
                <v-select
                  v-else
                  v-model="group.centerId"
                  :items="editAvailableCenters(gi)"
                  item-text="text"
                  item-value="value"
                  placeholder="Select Center"
                  outlined
                  dense
                  hide-details
                  class="center-select"
                  @change="onEditGroupCenterChange(gi)"
                ></v-select>
                <v-select
                  v-if="group.centerId && group.readonly"
                  :value="group.batchIds"
                  :items="group.batchOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                  multiple
                  chips
                  small-chips
                  disabled
                  class="batch-select"
                ></v-select>
                <v-select
                  v-else-if="group.centerId"
                  v-model="group.batchIds"
                  :items="group.batchOptions"
                  item-text="text"
                  item-value="value"
                  placeholder="Select Batches"
                  outlined
                  dense
                  hide-details
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  class="batch-select"
                ></v-select>
                <v-btn
                  v-if="editableEditGroups > 1 && !group.readonly"
                  icon
                  small
                  color="red"
                  @click="removeEditGroup(gi)"
                >
                  <v-icon small>mdi-minus-circle</v-icon>
                </v-btn>
              </div>
              <InputError :data="editFormErrors.batches" />
              <v-btn
                text
                small
                color="primary"
                class="mt-2"
                @click="addEditGroup"
                :disabled="editableEditGroups >= centers.length"
              >
                <v-icon small left>mdi-plus-circle</v-icon>
                Add Center
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditDialog">Cancel</v-btn>
          <v-btn color="primary" class="ma-2 cust-btn" @click="confirmEdit" :loading="editSaving">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" persistent max-width="400px">
      <v-card>
        <v-card-title class="headline">Delete Timetable</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ deletingItem ? deletingItem.original_name : '' }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" class="cust-btn" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="red" dark class="cust-btn" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import constants from "@/utils/constants.js";

const user = JSON.parse(sessionStorage.getItem("user"));

export default {
  name: "TimetableList",
  data() {
    return {
      timetableList: [],
      centers: [],
      filterBatchOptions: [],
      selectedCenterFilterIds: [],
      selectedBatchFilterIds: [],
      statusFilter: "active",
      statusOptions: [
        { text: "Active", value: "active" },
        { text: "Deleted", value: "deleted" },
        { text: "All", value: "all" },
      ],
      hasLoaded: false,
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,

      orderBy: {
        field: "created_at",
        order: "DESC",
      },

      // Add dialog
      addDialog: false,
      selectedFile: null,
      centerBatchGroups: [{ centerId: null, batchIds: [], batchOptions: [] }],
      uploading: false,
      formErrors: {
        file: null,
        batches: null,
      },

      // Edit dialog
      editDialog: false,
      editingItem: null,
      editName: "",
      editCenterBatchGroups: [{ centerId: null, batchIds: [], batchOptions: [] }],
      editSaving: false,
      editFormErrors: { batches: null },

      // Delete dialog
      deleteDialog: false,
      deletingItem: null,
    };
  },
  computed: {
    isCoordinator() {
      return user && Number(user.role_id) === constants.ROLES_ID.CO;
    },
    isTeacher() {
      return user && Number(user.role_id) === constants.ROLES_ID.TR;
    },
    isRestrictedRole() {
      return this.isCoordinator || this.isTeacher;
    },
    assignedCenterIds() {
      if (!this.isRestrictedRole) return [];
      return this.centers.map(function(c) { return c.value; });
    },
    editableEditGroups() {
      return this.editCenterBatchGroups.filter(function(g) { return !g.readonly; }).length;
    },
    totalPages() {
      return Math.ceil(this.totalRecords / this.pageSize);
    },
    headers() {
      return [
        { text: "File Name", value: "original_name" },
        { text: "Center / Batches", value: "", hideSort: true },
        { text: "Uploaded On", value: "created_at" },
        { text: "Uploaded By", value: "uploaded_by", hideSort: true },
        { text: "", value: "", type: "blank", class: "" },
      ];
    },
  },
  mounted() {
    this.requestGetCenters();
    this.loadTimetableList();
  },
  methods: {
    ...mapActions({
      getCenters: "centerAndBatchStore/getCenters",
      getCentersByUser: "centerAndBatchStore/getCentersByUser",
      getBatches: "centerAndBatchStore/getBatches",
      getTimetableList: "timetableStore/getTimetableList",
      addTimetable: "timetableStore/addTimetable",
      editTimetableAction: "timetableStore/editTimetable",
      deleteTimetableAction: "timetableStore/deleteTimetable",
    }),

    canDeleteTimetable(item) {
      if (!this.isRestrictedRole) return true;
      if (!item.centers_batches || !item.centers_batches.length) return true;
      var ids = this.assignedCenterIds;
      return item.centers_batches.every(function(cb) {
        return ids.indexOf(cb.center_id) !== -1;
      });
    },

    purgeDate(updatedAt) {
      if (!updatedAt) return "";
      var d = new Date(updatedAt);
      d.setDate(d.getDate() + 15);
      var dd = String(d.getDate()).padStart(2, "0");
      var mm = String(d.getMonth() + 1).padStart(2, "0");
      var yyyy = d.getFullYear();
      return dd + "-" + mm + "-" + yyyy;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      var d = new Date(dateStr);
      var dd = String(d.getDate()).padStart(2, "0");
      var mm = String(d.getMonth() + 1).padStart(2, "0");
      var yyyy = d.getFullYear();
      return dd + "-" + mm + "-" + yyyy;
    },

    requestGetCenters() {
      const action =
        user && (Number(user.role_id) === constants.ROLES_ID.CO || Number(user.role_id) === constants.ROLES_ID.TR)
          ? "getCentersByUser"
          : "getCenters";

      this[action]()
        .then((response) => {
          let list = [];
          if (response && response.length) {
            list = response;
          } else if (response && response.list && response.list.length) {
            list = response.list;
          }
          this.centers = list.map((x) => ({
            text: x.name,
            value: x.id,
          }));
        })
        .catch(() => {});
    },

    loadFilterBatches(centerIds) {
      this.getBatches({ center_id: centerIds })
        .then((response) => {
          if (response && response.list && response.list.length) {
            this.filterBatchOptions = response.list.map((x) => ({
              text: x.name,
              value: x.id,
            }));
          }
        })
        .catch(() => {});
    },

    loadTimetableList() {
      this.$root.showLoader = true;
      this.hasLoaded = false;

      const params = {
        pagination: {
          pageSize: this.pageSize,
          page: this.currentPage,
        },
      };

      if (this.orderBy) {
        params.orderBy = this.orderBy;
      }
      if (this.statusFilter) {
        params.status = this.statusFilter;
      }

      if (this.selectedCenterFilterIds.length) {
        params.center_ids = this.selectedCenterFilterIds;
      }
      if (this.selectedBatchFilterIds.length) {
        params.batch_ids = this.selectedBatchFilterIds;
      }

      this.getTimetableList(params)
        .then((response) => {
          this.$root.showLoader = false;
          this.hasLoaded = true;
          this.timetableList = (response && response.list) || [];
          var pg = response && response.pagination;
          this.totalRecords = pg ? pg.rowCount : 0;
        })
        .catch(() => {
          this.$root.showLoader = false;
          this.hasLoaded = true;
          this.timetableList = [];
        });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadTimetableList();
    },

    onHeaderAction() {},

    onStatusFilterChange() {
      this.currentPage = 1;
      this.loadTimetableList();
    },

    onCenterFilterChange() {
      this.selectedBatchFilterIds = [];
      this.filterBatchOptions = [];
      if (this.selectedCenterFilterIds.length) {
        this.loadFilterBatches(this.selectedCenterFilterIds);
      }
      this.currentPage = 1;
      this.loadTimetableList();
    },

    onBatchFilterChange() {
      this.currentPage = 1;
      this.loadTimetableList();
    },

    onSortChange() {
      this.currentPage = 1;
      this.loadTimetableList();
    },

    onViewClick(item) {
      window.open(item.file_url, "_blank");
    },

    onDeleteClick(item) {
      this.deletingItem = item;
      this.deleteDialog = true;
    },

    confirmDelete() {
      this.$root.showLoader = true;
      this.deleteTimetableAction({ timetable_id: this.deletingItem.id })
        .then(() => {
          this.$root.showLoader = false;
          this.deleteDialog = false;
          this.deletingItem = null;
          this.$toasted.show("Timetable deleted successfully", { type: "success" });
          this.loadTimetableList();
        })
        .catch(() => {
          this.$root.showLoader = false;
        });
    },

    // Edit
    onEditClick(item) {
      this.editingItem = item;
      this.editName = item.original_name;
      this.editFormErrors = { batches: null };
      this.editSaving = false;

      // Pre-populate center/batch groups from item.centers_batches
      if (item.centers_batches && item.centers_batches.length) {
        var self = this;
        this.editCenterBatchGroups = item.centers_batches.map(function(cb) {
          var isReadonly = self.isRestrictedRole && self.assignedCenterIds.indexOf(cb.center_id) === -1;
          return {
            centerId: cb.center_id,
            centerName: cb.center_name,
            batchIds: cb.batches.map(function(b) { return b.id; }),
            batchOptions: isReadonly
              ? cb.batches.map(function(b) { return { text: b.name, value: b.id }; })
              : [],
            readonly: isReadonly,
          };
        });
        // Load batch options for each pre-populated center
        for (var i = 0; i < this.editCenterBatchGroups.length; i++) {
          this.loadEditGroupBatches(i);
        }
      } else {
        this.editCenterBatchGroups = [{ centerId: null, batchIds: [], batchOptions: [] }];
      }

      this.editDialog = true;
    },

    closeEditDialog() {
      this.editDialog = false;
      this.editingItem = null;
      this.editCenterBatchGroups = [{ centerId: null, batchIds: [], batchOptions: [] }];
      this.editFormErrors = { batches: null };
    },

    editAvailableCenters(groupIndex) {
      var selectedIds = this.editCenterBatchGroups
        .filter(function(_, i) { return i !== groupIndex; })
        .map(function(g) { return g.centerId; })
        .filter(Boolean);
      return this.centers.filter(function(c) {
        return selectedIds.indexOf(c.value) === -1;
      });
    },

    loadEditGroupBatches(gi) {
      var group = this.editCenterBatchGroups[gi];
      if (!group.centerId) return;

      this.getBatches({ center_id: [group.centerId] })
        .then((response) => {
          if (response && response.list && response.list.length) {
            group.batchOptions = response.list.map((x) => ({
              text: x.name,
              value: x.id,
            }));
          }
        })
        .catch(() => {});
    },

    onEditGroupCenterChange(gi) {
      var group = this.editCenterBatchGroups[gi];
      group.batchIds = [];
      group.batchOptions = [];
      this.loadEditGroupBatches(gi);
    },

    addEditGroup() {
      this.editCenterBatchGroups.push({ centerId: null, batchIds: [], batchOptions: [] });
    },

    removeEditGroup(gi) {
      this.editCenterBatchGroups.splice(gi, 1);
    },

    confirmEdit() {
      this.editFormErrors = { batches: null };

      if (!this.editName || !this.editName.trim()) return;

      var batches = [];
      var hasValidEditableGroup = false;
      for (var i = 0; i < this.editCenterBatchGroups.length; i++) {
        var group = this.editCenterBatchGroups[i];
        if (group.centerId && group.batchIds.length) {
          if (!group.readonly) hasValidEditableGroup = true;
          for (var j = 0; j < group.batchIds.length; j++) {
            batches.push({
              center_id: group.centerId,
              batch_id: group.batchIds[j],
            });
          }
        }
      }

      if (!hasValidEditableGroup) {
        this.editFormErrors.batches = "Select at least one center with batches";
        return;
      }

      this.editSaving = true;
      this.editTimetableAction({
        timetable_id: this.editingItem.id,
        name: this.editName.trim(),
        batches: batches,
      })
        .then(() => {
          this.editSaving = false;
          this.closeEditDialog();
          this.$toasted.show("Timetable updated successfully", { type: "success" });
          this.loadTimetableList();
        })
        .catch(() => {
          this.editSaving = false;
        });
    },

    // Add Dialog
    openAddDialog() {
      this.addDialog = true;
      this.selectedFile = null;
      this.centerBatchGroups = [{ centerId: null, batchIds: [], batchOptions: [] }];
      this.formErrors = { file: null, batches: null };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    closeAddDialog() {
      this.addDialog = false;
      this.selectedFile = null;
      this.centerBatchGroups = [{ centerId: null, batchIds: [], batchOptions: [] }];
      this.formErrors = { file: null, batches: null };
    },

    onFileChange(e) {
      const file = e.target.files[0];
      if (file && file.type !== "application/pdf") {
        this.formErrors.file = "Only PDF files are allowed";
        this.selectedFile = null;
        return;
      }
      this.formErrors.file = null;
      this.selectedFile = file;
    },

    clearFile() {
      this.selectedFile = null;
      this.formErrors.file = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    availableCenters(groupIndex) {
      var selectedIds = this.centerBatchGroups
        .filter(function(_, i) { return i !== groupIndex; })
        .map(function(g) { return g.centerId; })
        .filter(Boolean);
      return this.centers.filter(function(c) {
        return selectedIds.indexOf(c.value) === -1;
      });
    },

    onGroupCenterChange(gi) {
      var group = this.centerBatchGroups[gi];
      group.batchIds = [];
      group.batchOptions = [];
      if (!group.centerId) return;

      this.getBatches({ center_id: [group.centerId] })
        .then((response) => {
          if (response && response.list && response.list.length) {
            group.batchOptions = response.list.map((x) => ({
              text: x.name,
              value: x.id,
            }));
          }
        })
        .catch(() => {});
    },

    addGroup() {
      this.centerBatchGroups.push({ centerId: null, batchIds: [], batchOptions: [] });
    },

    removeGroup(gi) {
      this.centerBatchGroups.splice(gi, 1);
    },

    submitTimetable() {
      this.formErrors = { file: null, batches: null };
      let valid = true;

      if (!this.selectedFile) {
        this.formErrors.file = "Please select a PDF file";
        valid = false;
      }

      var batches = [];
      var hasValidGroup = false;
      for (var i = 0; i < this.centerBatchGroups.length; i++) {
        var group = this.centerBatchGroups[i];
        if (group.centerId && group.batchIds.length) {
          hasValidGroup = true;
          for (var j = 0; j < group.batchIds.length; j++) {
            batches.push({
              center_id: group.centerId,
              batch_id: group.batchIds[j],
            });
          }
        }
      }

      if (!hasValidGroup) {
        this.formErrors.batches = "Select at least one center with batches";
        valid = false;
      }

      if (!valid) return;

      this.uploading = true;

      var formData = new FormData();
      formData.append("file", this.selectedFile);
      formData.append("batches", JSON.stringify(batches));

      this.addTimetable(formData)
        .then(() => {
          this.uploading = false;
          this.closeAddDialog();
          this.$toasted.show("Timetable uploaded successfully", { type: "success" });
          this.loadTimetableList();
        })
        .catch(() => {
          this.uploading = false;
        });
    },
  },
};
</script>
<style>
.timetable-list-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.timetable-list-page .timetable-top-bar {
  flex-shrink: 0;
  margin-bottom: 12px;
}
.timetable-list-page .timetable-top-bar-inner {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}
.timetable-list-page .filter-section {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
}
.timetable-list-page .filter-section .v-input {
  width: 240px;
  font-size: 13px;
}
.timetable-list-page .filter-section .v-input:first-child {
  width: 140px;
}
.timetable-list-page .normal-card.autohg {
  flex: 1;
  width: 100%;
  height: auto !important;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.timetable-list-page .data-table-section {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.timetable-list-page .cust-table {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.timetable-list-page .v-data-table__wrapper {
  flex: 1;
  overflow-y: auto !important;
}
.timetable-list-page .cust-table table {
  min-width: 700px;
}
.timetable-list-page .cust-table thead.v-data-table-header th {
  background: #FAFAFA !important;
}
.timetable-list-page .deleted-row {
  background-color: #fff5f5 !important;
}
.timetable-list-page .deleted-row td {
  color: #b71c1c;
  opacity: 0.7;
}
.timetable-list-page .deletion-date {
  font-size: 11px;
  color: #c62828;
  margin-top: 2px;
  font-style: italic;
}
.timetable-list-page .center-batch-group {
  margin-bottom: 4px;
}
.timetable-list-page .center-batch-group:last-child {
  margin-bottom: 0;
}
.timetable-list-page .center-batch-group .center-name {
  font-weight: 600;
  font-size: 13px;
}
.timetable-list-page .center-batch-group .batch-list {
  font-size: 12px;
  color: #666;
  padding-left: 8px;
}
.timetable-list-page .pagination-section {
  padding: 10px 0;
  display: flex;
  justify-content: center;
}
.timetable-list-page .actions-col {
  white-space: nowrap;
  width: 110px;
  min-width: 110px;
}
/* Dialog styles - not scoped under .timetable-list-page since v-dialog renders at body level */
.add-timetable-form .form-row {
  margin-bottom: 16px;
}
.add-timetable-form .form-row label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  font-size: 13px;
}
.add-timetable-form .file-upload-area {
  display: flex;
  align-items: center;
  border: 1px dashed #ccc;
  border-radius: 4px;
  padding: 10px 12px;
  cursor: pointer;
  font-size: 13px;
  min-height: 40px;
}
.add-timetable-form .file-upload-area:hover {
  border-color: #999;
  background: #fafafa;
}
.add-timetable-form .center-batch-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}
.add-timetable-form .center-batch-row .center-select {
  flex: 0 0 220px;
  max-width: 220px;
}
.add-timetable-form .center-batch-row .batch-select {
  flex: 1;
  min-width: 0;
}
.add-timetable-form .center-batch-row .v-btn--icon {
  flex-shrink: 0;
  margin-top: 4px;
}
</style>
